import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Navigate, Link } from "react-router-dom";
import MainWrapper from "../../MainWrapper";
import { Table, Spinner } from "react-bootstrap";
import Icon from "react-icons-kit";
import { ic_arrow_back } from "react-icons-kit/md";
import Loader from "../../layouts/Loader";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../../../config";

// Drill-down view opened from the "Potential Lost Customers" dashboard widget.
//
// On mount it bulk-acknowledges the active alerts in scope (so the widget's
// flames clear), then lists them: Property Name, Salesperson, Last Order Date.
// Respects the role's dataScope ("self" => only the logged-in salesperson's
// properties), read from the role-permission payload the sidebar already loads.
const PotentialLostCustomers = ({
  auth: { auth, isAuthenticated },
  Admin: { userMenuList },
}) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  // lostCustomerAlertId currently being acknowledged (shows a per-row spinner).
  const [acknowledgingId, setAcknowledgingId] = useState(null);

  const token = auth && auth[0] ? auth[0].token : null;
  const userId = auth && auth[0] ? auth[0].userId : null;

  const dashboard =
    userMenuList && userMenuList[0] ? userMenuList[0].dashboard : null;
  const dataScope = (dashboard && dashboard.dataScope) || "all";
  const scopedSalesPersonId = dataScope === "self" ? userId : null;

  useEffect(() => {
    if (!token) return;
    setLoading(true);

    const ackParams = { userId };
    if (scopedSalesPersonId) ackParams.salesPersonId = scopedSalesPersonId;
    const getParams = { userId };
    if (scopedSalesPersonId) getParams.salesPersonId = scopedSalesPersonId;

    // Acknowledge first (clears the widget flames), then load the list.
    axios
      .post(`${API_URL}/User/api/LostCustomerParameter/AcknowledgeAllAlerts`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: ackParams,
      })
      .catch((err) => {
        console.warn("Could not acknowledge lost-customer alerts:", err.message);
      })
      .finally(() => {
        axios
          .get(`${API_URL}/User/api/LostCustomerParameter/GetAlerts`, {
            headers: { Authorization: `Bearer ${token}` },
            params: getParams,
          })
          .then((res) => {
            const rows = Array.isArray(res.data)
              ? res.data
              : (res.data && res.data.result) || [];
            setAlerts(rows);
          })
          .catch((err) => {
            console.warn("Could not load potential lost customers:", err.message);
          })
          .finally(() => setLoading(false));
      });
  }, [token, scopedSalesPersonId]);

  if (!isAuthenticated) return <Navigate to="/login" />;

  // Per-row "Acknowledge" action: POST to AddAcknowledgement, then drop the
  // acknowledged row from the list on success.
  const handleAcknowledgeClick = (row) => {
    console.log("Acknowledge clicked", row);
    if (!token || acknowledgingId != null) return;
    setAcknowledgingId(row.lostCustomerAlertId);
    const payload = { contactInfoId: row.contactInfoId, userId };
    axios
      .post(`${API_URL}/User/api/User/AddAcknowledgement`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        toast.success("Acknowledged.");
        setAlerts((prev) =>
          prev.filter((a) => a.lostCustomerAlertId !== row.lostCustomerAlertId)
        );
      })
      .catch((err) => {
        console.warn("Could not acknowledge customer:", err.message);
        toast.error("Could not acknowledge. Please try again.");
      })
      .finally(() => setAcknowledgingId(null));
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const year = d.getFullYear();
    return `${month < 10 ? "0" + month : month}-${day < 10 ? "0" + day : day}-${year}`;
  };

  return (
    <Fragment>
      <MainWrapper>
        <Loader loading={loading}>
          <div className="container">
            <div className="sub-header mt-5 mb-3">
              <h2>Potential Lost Customers</h2>
              <Link className="btn btn-primary me-1" to="/dashboard">
                <Icon icon={ic_arrow_back} />Back
              </Link>
            </div>
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>Property Name</th>
                  <th>Salesperson</th>
                  <th>Last Order Date</th>
                  <th>Days Since Last Order</th>
                  <th>Acknowledge</th>
                </tr>
              </thead>
              <tbody>
                {alerts.length > 0 ? (
                  alerts.map((row) => (
                    <tr key={row.lostCustomerAlertId}>
                      <td>{row.propertyName || "—"}</td>
                      <td>{row.salesPersonName || "—"}</td>
                      <td>{formatDate(row.lastOrderDate)}</td>
                      <td>{row.daysSinceLastOrder}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => handleAcknowledgeClick(row)}
                          disabled={acknowledgingId === row.lostCustomerAlertId}
                        >
                          {acknowledgingId === row.lostCustomerAlertId ? (
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                          ) : (
                            "Acknowledge"
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center">
                      No potential lost customers.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Loader>
      </MainWrapper>
    </Fragment>
  );
};

const mapStateToProps = ({ auth, Admin }) => ({ auth, Admin });

export default connect(mapStateToProps)(PotentialLostCustomers);
